/**
 * @module components/FreezePanes
 * @description 表格冻结窗格组件
 *
 * 状态管理模式：
 *   读: useSettingObjectState('freeze')
 *   写: setValue 整体替换 freeze 对象
 */

import React, { useCallback } from 'react';
import { useIntl } from 'umi';
import { Switch } from 'tea-component';
import NumberInput from '@/pages/datalab/components/NumberInput';
import ConfigCard from '@/pages/datalab/components/ConfigCard';
import type { IFreezeSetting } from '../../types/settings';
import { useSettingObjectState } from '../../context';
import './index.scss';

interface FreezePanesProps {
  visibleItems?: Record<string, boolean>;
}

const MIN_FREEZE_COUNT = 1;
const MAX_FREEZE_COUNT = 5;

/** 列数合法范围 [1, 5] */
const clampFreezeCount = (count: number): number =>
  Math.min(Math.max(count, MIN_FREEZE_COUNT), MAX_FREEZE_COUNT);

const FreezePanes: React.FC<FreezePanesProps> = () => {
  const { formatMessage } = useIntl();
  const { value: freezeVal, setValue: setFreeze } =
    useSettingObjectState('freeze');

  const { show, count } = freezeVal;
  const displayCount = clampFreezeCount(count);

  const handleFreezeUpdate = useCallback(
    (next: IFreezeSetting) => {
      setFreeze({
        show: next.show,
        count: next.show ? clampFreezeCount(next.count) : MIN_FREEZE_COUNT,
      });
    },
    [setFreeze],
  );

  const handleSwitchChange = useCallback(
    (nextShow: boolean): void => {
      handleFreezeUpdate({
        show: nextShow,
        count: nextShow ? clampFreezeCount(count) : MIN_FREEZE_COUNT,
      });
    },
    [count, handleFreezeUpdate],
  );

  const handleCountChange = useCallback(
    (value: string): void => {
      if (!value) {
        return;
      }
      const num = Number(value);
      if (Number.isInteger(num)) {
        handleFreezeUpdate({ show, count: num });
      }
    },
    [show, handleFreezeUpdate],
  );

  return (
    <ConfigCard
      className="dbl-chart-freeze-panes"
      title={<span>{formatMessage({ id: 'dbl_chart_freeze_panes' })}</span>}
      operation={<Switch value={show} onChange={handleSwitchChange} />}
    >
      {show && (
        <div className="dbl-chart-freeze-panes-content">
          {formatMessage({ id: 'dbl_chart_freeze_panes_first' })}
          <NumberInput
            type="number"
            value={String(displayCount)}
            min={MIN_FREEZE_COUNT}
            max={MAX_FREEZE_COUNT}
            allowNegative={false}
            allowDecimal={false}
            allowEmpty={displayCount !== MIN_FREEZE_COUNT}
            size="s"
            style={{ width: 85 }}
            onChange={handleCountChange}
          />
          {formatMessage({ id: 'dbl_chart_freeze_columns' })}
        </div>
      )}
    </ConfigCard>
  );
};

export default React.memo(FreezePanes);
